import FadeUp from "./FadeUp";

const timeline = [
    {
        year: "2018",
        title: "Started freelancing",
        body: "No job, no funds, no plan. Picked influencer marketing on a hunch and never looked back.",
    },
    {
        year: "2019–2022",
        title: "Early ecommerce work",
        body: "One-off campaigns for small ecommerce brands. Enjoyed the work but wanted something more compounding.",
    },
    {
        year: "2022–2026",
        title: "Sunsama · Head of Partnerships",
        body: "Joined as Influencer Manager, became Head of Partnerships. Built the ambassador program from zero.",
    },
    {
        year: "2022–2026",
        title: "SARAL · Operations Lead",
        body: "In parallel, worked with SARAL and 40+ brands across ecommerce, B2B SaaS, and B2C SaaS.",
    },
    {
        year: "2026",
        title: "AV Consultation",
        body: "Started AV Consultation to help productivity apps build a creator engine as a long-term growth asset.",
    },
];

const milestones = [
    { value: "70+", label: "Brands worked with" },
    { value: "1,000+", label: "Campaigns run" },
    { value: "8+ yrs", label: "In the industry" },
];

export const About = () => (
    <section
        id="about"
        data-testid="about-section"
        className="py-24 md:py-32 bg-white"
    >
        <div className="container-page">
            <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-14 lg:gap-20 items-start">
                <div>
                    <FadeUp>
                        <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                            About
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.05}>
                        <div className="mt-6 flex items-center gap-4">
                            <div className="h-16 w-16 rounded-full overflow-hidden border border-black/[0.08] bg-[#FAFAFA]">
                                <img
                                    src="/ambuj.jpg"
                                    alt="Ambuj Vats"
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <div>
                                <div className="font-display font-bold text-[17px] text-[#0A0A0A] leading-[1.3]">
                                    Ambuj Vats
                                </div>
                                <div className="text-[13px] text-gray-500">
                                    Operator. Not an agency.
                                </div>
                            </div>
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.1}>
                        <div className="mt-8 space-y-4 text-[15.5px] md:text-base text-gray-700 leading-relaxed">
                            <p>
                                I started working as a freelancer in 2018. I had no job, no funds, and no plan. There seemed to be a plethora of services I could have picked up back then.
                            </p>
                            <p>
                                But for some reason, I thought I'd try influencer marketing for a while and see how it goes.
                            </p>
                            <p>
                                It was operations-heavy, and the operator matters a lot. That's probably why I picked this niche. To feel like I was making some kind of difference.
                            </p>
                            <p>
                                Some of my initial clients were small ecommerce companies. I enjoyed working with them, but they were mostly one-off campaigns that got over in a few weeks.
                            </p>
                            <p>
                                My breakthrough came with Sunsama, where I joined as an Influencer Manager and eventually became the Head of Partnerships. I worked at Sunsama for four years, built their ambassador program from scratch, and contributed to the company's growth.
                            </p>
                            <p>
                                In parallel, I also worked with SARAL, an influencer marketing tool, where I worked with 40+ brands across ecommerce, B2B SaaS, and B2C SaaS.
                            </p>
                            <p>
                                These four years taught me how to create systems around influencers. How to build programs from scratch. And how to make influencer marketing one of the most important growth channels for a brand.
                            </p>
                            <p>
                                In early 2026, I started AV Consultation to help productivity apps build an influencer engine that becomes a long-term growth asset, not just another marketing channel.
                            </p>
                            <p className="text-[#0A0A0A] font-medium">
                                My goal is never to work forever and get paid a retainer. It's to build a system that your team can eventually take in-house and keep growing long after I'm gone.
                            </p>
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.2}>
                        <div className="mt-8 grid grid-cols-3 gap-3">
                            {milestones.map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={`about-milestone-${i}`}
                                    className="rounded-2xl border border-black/[0.08] bg-[#FAFAFA] p-4"
                                >
                                    <div className="font-display font-extrabold text-2xl md:text-[28px] tracking-[-0.015em] text-[#0A0A0A] leading-[1.3]">
                                        {m.value}
                                    </div>
                                    <div className="mt-1.5 text-[12px] text-gray-500 leading-[1.65]">
                                        {m.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>

                <FadeUp delay={0.15}>
                    <div className="lg:sticky lg:top-32 relative rounded-3xl border border-black/[0.08] bg-[#FAFAFA] p-6 md:p-8">
                        <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-gray-500">
                            Journey
                        </p>
                        <div className="mt-6 relative">
                            <div className="absolute left-[19px] top-1 bottom-1 w-px bg-black/[0.08]" />
                            <div className="space-y-6">
                                {timeline.map((t, i) => (
                                    <div key={i} className="relative flex gap-5">
                                        <div className="relative z-10 flex-none h-10 w-10 rounded-full bg-white border-[3px] border-[#0A0A0A] flex items-center justify-center">
                                            <div className="h-1.5 w-1.5 rounded-full bg-[#0A0A0A]" />
                                        </div>
                                        <div className="pt-1.5">
                                            <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-gray-500">
                                                {t.year}
                                            </div>
                                            <div className="mt-1 font-display font-bold text-[16px] md:text-[17px] tracking-tight text-[#0A0A0A] leading-[1.3]">
                                                {t.title}
                                            </div>
                                            <div className="mt-2 text-[14px] text-gray-600 leading-relaxed">
                                                {t.body}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </FadeUp>
            </div>
        </div>
    </section>
);

export default About;
